import { CONFIG, DERIVED_CONFIG, debugLog, productionLog } from './config.js';

class ApiClient {
    constructor() {
        debugLog('API Client initialized');
    }

    async validateToken(token) {
        try {
            const response = await fetch(DERIVED_CONFIG.ENDPOINTS.VALIDATE_TOKEN, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'X-App-Unique-Id': CONFIG.APP_UNIQUE_ID
                }
            });
            
            if (!response.ok) {
                return false;
            }
            
            const result = await response.json();
            return result.status === 'success';
        } catch (error) {
            productionLog.error('Token validation error:', error);
            return false;
        }
    }

    redirectToLogin() {
        // Store the current URL for redirecting back after login
        if (window.location.pathname !== '/') {
            sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
        }
        window.location.href = '/';
    }

    async request(method, endpoint, data = null, requiresAuth = true) {
        const url = `${endpoint}`;
        const token = localStorage.getItem(CONFIG.TOKEN_STORAGE_KEY);
        
        // If no token and auth is required, redirect to login
        if (requiresAuth && !token) {
            this.redirectToLogin();
            return Promise.reject(new Error('Authentication required'));
        }

        // Validate session before making authenticated requests
        if (requiresAuth && token) {
            const isValidSession = await this.ensureValidSession();
            if (!isValidSession) {
                return Promise.reject(new Error('Session validation failed'));
            }
        }

        const headers = new Headers({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });

        if (token) {
            headers.append('Authorization', `Bearer ${token}`);
        }

    
        const options = {
            method,
            headers,
            credentials: 'include', // allows browser to send client certificates
            mode: 'cors',
            cache: 'no-cache'
        };

        if (data) {
            options.body = JSON.stringify(data);
        }

        try {
            // Create a timeout promise
            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error(`Request timed out after ${CONFIG.API_TIMEOUT}ms`)), CONFIG.API_TIMEOUT)
            );

            // Race the fetch against the timeout
            const response = await Promise.race([
                fetch(url, options),
                timeoutPromise
            ]);
            
            if (response.status === 401) {
                this.logout();
                this.redirectToLogin();
                return Promise.reject(new Error('Session expired. Please log in again.'));
            }
            
            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                    debugLog('API error response:', errorData);
                } catch (e) {
                    errorData = { message: response.statusText };
                }
                const error = new Error(errorData.message || `API request failed with status ${response.status}`);
                error.status = response.status;
                error.data = errorData;
                throw error;
            }
            
            // Handle empty responses
            const text = await response.text();
            const responseData = text ? JSON.parse(text) : {};
            
            // Debug log successful responses
            debugLog(`API ${method} ${endpoint}`, { response: responseData });
            
            return responseData;
        } catch (error) {
            productionLog.error('API request error:', error);
            throw error;
        }
    }

    // Session validation methods
    async validateSession() {
        const token = localStorage.getItem(CONFIG.TOKEN_STORAGE_KEY);
        if (!token) {
            return false;
        }

        try {
            // Make direct request without session validation to avoid infinite recursion
            const url = `${DERIVED_CONFIG.ENDPOINTS.VALIDATE_TOKEN}`;
            const headers = new Headers({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-Requested-With': 'XMLHttpRequest'
            });

            const options = {
                method: 'GET',
                headers,
                credentials: 'include',
                mode: 'cors',
                cache: 'no-cache'
            };

            const response = await fetch(url, options);
            
            if (response.status === 401) {
                return false;
            }

            const responseData = await response.json();
            return responseData.status === 'success';
        } catch (error) {
            debugLog('Session validation failed:', error);
            return false;
        }
    }

    async ensureValidSession() {
        const token = localStorage.getItem(CONFIG.TOKEN_STORAGE_KEY);
        if (!token) {
            this.redirectToLogin();
            return false;
        }

        // Validate session before making request
        const isValid = await this.validateSession();
        if (!isValid) {
            this.logout();
            this.redirectToLogin();
            return false;
        }

        return true;
    }

    // Helper methods for common HTTP methods
    get(endpoint, requiresAuth = true) {
        return this.request('GET', endpoint, null, requiresAuth);
    }

    post(endpoint, data = {}, requiresAuth = true) {
        return this.request('POST', endpoint, data, requiresAuth);
    }

    put(endpoint, data = {}, requiresAuth = true) {
        return this.request('PUT', endpoint, data, requiresAuth);
    }

    delete(endpoint, requiresAuth = true) {
        return this.request('DELETE', endpoint, null, requiresAuth);
    }

    // Authentication methods
    async login(credentials) {
        try {
            debugLog('Attempting login...');
            const response = await this.post('/auth/token', credentials, false);
            
            if (response && response.token) {
                debugLog('Login successful, storing token');
                localStorage.setItem(CONFIG.TOKEN_STORAGE_KEY, response.token);
                
                // Store additional user info if available
                if (response.user) {
                    localStorage.setItem(`${CONFIG.TOKEN_STORAGE_KEY}_user`, JSON.stringify(response.user));
                }
                
                // Get user info to verify the token is valid
                try {
                    const userInfo = await this.get('/auth/me');
                    debugLog('User info retrieved:', userInfo);
                    
                    // Store the updated user info
                    if (userInfo) {
                        localStorage.setItem(`${CONFIG.TOKEN_STORAGE_KEY}_user`, JSON.stringify(userInfo));
                    }
                    
                    // Redirect back to the original URL if available
                    const redirectUrl = sessionStorage.getItem('redirectAfterLogin') || '/docs/';
                    sessionStorage.removeItem('redirectAfterLogin');
                    window.location.href = redirectUrl;
                    
                    return { ...response, user: userInfo };
                } catch (error) {
                    productionLog.error('Failed to fetch user info:', error);
                    throw new Error('Login successful but failed to fetch user information');
                }
            }
            throw new Error('Invalid response from server: No token received');
        } catch (error) {
            productionLog.error('Login failed:', error);
            throw error;
        }
    }

    logout() {
        debugLog('Logging out...');
        localStorage.removeItem(CONFIG.TOKEN_STORAGE_KEY);
        localStorage.removeItem(`${CONFIG.TOKEN_STORAGE_KEY}_user`);
        this.redirectToLogin();
    }

    async isAuthenticated() {
        const token = localStorage.getItem(CONFIG.TOKEN_STORAGE_KEY);
        if (!token) return false;
        
        // For API tokens, we only need to validate with the server
        try {
            const isValid = await this.validateToken(token);
            if (!isValid) {
                this.logout();
                return false;
            }
            return true;
        } catch (e) {
            debugLog('Error validating token:', e);
            this.logout();
            return false;
        }
    }

    // Archived versions methods
    async getArchivedVersions() {
        try {
            const response = await this.get('/api/openapi/archived');
            return response;
        } catch (error) {
            productionLog.error('Failed to fetch archived versions:', error);
            throw error;
        }
    }

    async getArchivedVersion(version) {
        try {
            const response = await this.get(`/api/openapi/archived/${version}`);
            return response;
        } catch (error) {
            productionLog.error(`Failed to fetch archived version ${version}:`, error);
            throw error;
        }
    }
}

// Helper function to get user info if available
function getUserInfo() {
    try {
        const userData = localStorage.getItem(`${CONFIG.TOKEN_STORAGE_KEY}_user`);
        return userData ? JSON.parse(userData) : null;
    } catch (e) {
        debugLog('Error parsing user data:', e);
        return null;
    }
}

// Create and export the singleton instance
const apiClient = new ApiClient();

// Export the instance and helper functions
export { apiClient, getUserInfo };

// For debugging
if (CONFIG.DEBUG_MODE) {
    window.__apiClient = apiClient;
    debugLog('API Client initialized in debug mode');
}